import React, { useState } from 'react';
import { Bell, Search, Palette, MessageSquare, X, CheckCheck, Languages, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Avatar, AvatarFallback } from './ui/avatar';
import { useTheme } from '../contexts/ThemeContext';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

const notifIcons = {
  new_message: '💬',
  new_customer: '👤',
  sale_complete: '💰',
  ai_response: '✨',
  campaign_update: '📢',
  system: '🔔'
};

// A dónde lleva cada notificación al pulsarla
const NOTIF_ROUTES = {
  new_message: '/inbox',
  ai_response: '/inbox',
  new_customer: '/customers',
  sale_complete: '/deals',
  quote_accepted: '/invoices',
  payment: '/escrow',
  escrow: '/escrow',
  ticket: '/tickets',
  ticket_created: '/tickets',
  escalation: '/tickets',
  automation: '/automations',
  campaign_update: '/campaigns',
  weekly_ranking: '/reports',
  system: '/settings',
};

export const routeForNotif = (notif) => {
  if (notif.chat_id) return '/inbox';
  if (notif.ticket_id) return '/tickets';
  if (notif.customer_id) return '/customers';
  if (notif.campaign_id) return '/campaigns';
  return NOTIF_ROUTES[notif.type] || '/analytics';
};

// Panel: hoja inferior en móvil, dropdown anclado en desktop
const panelShell = "absolute z-50 right-3 sm:right-6 top-full mt-2 w-[min(94vw,22rem)] " +
  "bg-card/95 backdrop-blur-xl border border-border rounded-2xl shadow-2xl animate-scale-in overflow-hidden";

export const Header = ({ title, subtitle, children }) => {
  const { theme, setTheme, themes } = useTheme();
  const { chats, setSelectedChat, notifications, fetchNotifications, markNotificationRead, markAllNotificationsRead } = useApp();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showActiveChats, setShowActiveChats] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  React.useEffect(() => {
    document.documentElement.lang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  }, [i18n.resolvedLanguage, i18n.language]);

  const unreadCount = chats.reduce((sum, chat) => sum + (chat.unread_count || 0), 0);
  const activeChats = chats.filter(c => c.last_message).slice(0, 8);
  const unreadNotifs = notifications.filter(n => !n.read).length;

  const closePanels = () => { setShowNotifications(false); setShowActiveChats(false); };

  const handleOpenChat = (chat) => {
    setSelectedChat(chat);
    navigate('/inbox');
    setShowActiveChats(false);
  };

  const cycleTheme = () => {
    const themeKeys = Array.isArray(themes) ? themes : Object.keys(themes);
    const nextTheme = themeKeys[(themeKeys.indexOf(theme) + 1) % themeKeys.length];
    setTheme(nextTheme);
    toast.success(`Tema: ${nextTheme}`);
  };

  const toggleLang = () => {
    const current = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
    const newLang = current === 'es' ? 'en' : 'es';
    i18n.changeLanguage(newLang);
    document.documentElement.lang = newLang;
    localStorage.setItem('deal-app-language', newLang);
    toast.success(newLang === 'es' ? 'Idioma: Español' : 'Language: English');
  };

  const readAllNotifs = async () => {
    await markAllNotificationsRead();
    fetchNotifications();
  };

  const handleNotifClick = (notif) => {
    markNotificationRead(notif.id);
    if (notif.chat_id) {
      const chat = chats.find(c => c.id === notif.chat_id);
      if (chat) setSelectedChat(chat);
    }
    navigate(routeForNotif(notif));
    setShowNotifications(false);
  };

  const runSearch = (value) => {
    const q = (value || '').trim();
    if (!q) return;
    navigate(`/customers?q=${encodeURIComponent(q)}`);
    setShowSearch(false);
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diff = (new Date() - d) / 1000 / 60;
    if (diff < 1) return 'Ahora';
    if (diff < 60) return `Hace ${Math.floor(diff)} min`;
    if (diff < 1440) return `Hace ${Math.floor(diff / 60)} h`;
    return d.toLocaleDateString('es');
  };

  const iconBtn = "relative h-8 w-8 sm:h-9 sm:w-9 shrink-0 touch-manipulation";

  return (
    <header data-testid="page-header" className="border-b border-border bg-card/90 backdrop-blur-md sticky top-0 z-30 safe-top">
      <div className="h-14 sm:h-16 flex items-center justify-between gap-2 px-3 sm:px-6">
        <div className="flex-1 min-w-0">
          <h1 data-testid="header-title" className="text-sm sm:text-lg font-semibold truncate">{title}</h1>
          {subtitle && <p data-testid="header-subtitle" className="text-[11px] sm:text-sm text-muted-foreground truncate">{subtitle}</p>}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 shrink-0">
          <div className="hidden sm:flex items-center gap-2 min-w-0 max-w-[40vw] overflow-x-auto no-scrollbar">
            {children}
          </div>

          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input data-testid="header-search" placeholder="Buscar clientes..." className="pl-9 w-44 lg:w-48 h-9 bg-muted border-none text-sm"
              title="Busca un cliente y pulsa Enter para abrir el listado"
              onKeyDown={(e) => { if (e.key === 'Enter') runSearch(e.currentTarget.value); }} />
          </div>

          {/* Search (mobile) */}
          <Button data-testid="header-search-toggle" variant="ghost" size="icon" className={cn(iconBtn, "md:hidden")}
            title="Buscar clientes" onClick={() => { setShowSearch(v => !v); closePanels(); }}>
            <Search className="w-4 h-4" />
          </Button>

          {/* Active Conversations */}
          <div>
            <Button
              data-testid="header-active-chats"
              variant="ghost"
              size="icon"
              className={iconBtn}
              title="Conversaciones activas — abre el Inbox unificado"
              onClick={() => { setShowActiveChats(!showActiveChats); setShowNotifications(false); setShowSearch(false); }}
            >
              <MessageSquare className="w-4 h-4" />
              {unreadCount > 0 && (
                <span className="absolute top-0 right-0 min-w-[14px] h-3.5 px-0.5 bg-primary text-primary-foreground text-[8px] leading-none font-bold rounded-full flex items-center justify-center pointer-events-none">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </Button>

            {showActiveChats && (
              <div className={cn(panelShell, "sm:w-80")} data-testid="header-chats-panel">
                <div className="p-3 border-b border-border flex items-center justify-between">
                  <span className="text-sm font-semibold">Conversaciones Activas</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowActiveChats(false)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <ScrollArea className="max-h-[50vh] sm:max-h-80">
                  <div className="p-2 space-y-1">
                    {activeChats.map((chat) => (
                      <button
                        key={chat.id}
                        data-testid={`header-chat-${chat.id}`}
                        className="w-full p-2.5 rounded-lg hover:bg-muted/50 flex items-center gap-3 text-left transition-colors"
                        onClick={() => handleOpenChat(chat)}
                      >
                        <Avatar className="h-9 w-9">
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {chat.customer?.name?.charAt(0) || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{chat.customer?.name || 'Cliente'}</p>
                          <p className="text-xs text-muted-foreground truncate">{chat.last_message || 'Sin mensajes'}</p>
                        </div>
                        {chat.unread_count > 0 && (
                          <Badge className="bg-primary text-primary-foreground text-xs h-5 min-w-5 px-1">
                            {chat.unread_count}
                          </Badge>
                        )}
                      </button>
                    ))}
                    {activeChats.length === 0 && (
                      <p className="text-center text-sm text-muted-foreground py-6">Sin conversaciones activas</p>
                    )}
                  </div>
                </ScrollArea>
                <div className="p-2 border-t border-border">
                  <Button data-testid="header-chats-open-inbox" variant="ghost" className="w-full justify-between text-sm h-10"
                    onClick={() => { setShowActiveChats(false); navigate('/inbox'); }}>
                    Ver todo el Inbox <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div>
            <Button
              data-testid="header-notifications"
              variant="ghost"
              size="icon"
              className={iconBtn}
              title="Notificaciones — cada aviso te lleva a la pantalla de la que informa"
              onClick={() => { setShowNotifications(!showNotifications); setShowActiveChats(false); setShowSearch(false); }}
            >
              <Bell className="w-4 h-4" />
              {unreadNotifs > 0 && (
                <span className="absolute top-0 right-0 min-w-[14px] h-3.5 px-0.5 bg-destructive text-destructive-foreground text-[8px] leading-none font-bold rounded-full flex items-center justify-center pointer-events-none">
                  {unreadNotifs > 9 ? '9+' : unreadNotifs}
                </span>
              )}
            </Button>

            {showNotifications && (
              <div className={cn(panelShell, "sm:w-96")} data-testid="header-notifications-panel">
                <div className="p-3 border-b border-border flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <span className="text-sm font-semibold">Notificaciones</span>
                    <p className="text-[11px] text-muted-foreground">{unreadNotifs} sin leer · pulsa un aviso y te llevo ahí</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {unreadNotifs > 0 && (
                      <Button data-testid="header-notifs-read-all" variant="ghost" size="sm" className="h-8 text-xs" onClick={readAllNotifs}>
                        <CheckCheck className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Leer todo</span>
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setShowNotifications(false)}>
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
                <ScrollArea className="max-h-[50vh] sm:max-h-96">
                  <div className="p-2 space-y-1">
                    {notifications.slice(0, 20).map((notif) => (
                      <div
                        key={notif.id}
                        data-testid={`header-notif-${notif.id}`}
                        className={cn(
                          "p-3 rounded-lg cursor-pointer transition-colors",
                          notif.read ? "hover:bg-muted/30" : "bg-primary/5 hover:bg-primary/10"
                        )}
                        onClick={() => handleNotifClick(notif)}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-lg">{notifIcons[notif.type] || '🔔'}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={cn("text-sm font-medium", !notif.read && "text-primary")}>{notif.title}</p>
                              {!notif.read && <span className="w-2 h-2 bg-primary rounded-full shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{notif.message}</p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {formatTime(notif.created_at)} · <span className="text-primary">Abrir {routeForNotif(notif).replace('/', '')}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                    {notifications.length === 0 && (
                      <p className="text-center text-sm text-muted-foreground py-6">Sin notificaciones por ahora</p>
                    )}
                  </div>
                </ScrollArea>
                <div className="p-2 border-t border-border">
                  <Button data-testid="header-notifs-see-all" variant="ghost" className="w-full justify-between text-sm h-10"
                    onClick={() => { setShowNotifications(false); navigate('/notifications'); }}>
                    Todas las notificaciones <ArrowRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <Button
            data-testid="language-toggle"
            variant="ghost"
            size="icon"
            className={iconBtn}
            title="Cambiar idioma de la app (Español / English)"
            onClick={toggleLang}
          >
            <Languages className="w-4 h-4" />
            <span className="absolute bottom-0 right-0 text-[7px] leading-none font-bold bg-muted rounded px-0.5 pointer-events-none">
              {i18n.language === 'es' ? 'ES' : 'EN'}
            </span>
          </Button>

          <Button data-testid="theme-toggle" variant="ghost" size="icon" className={iconBtn}
            title={`Tema actual: ${theme} — pulsa para cambiar`} onClick={cycleTheme}>
            <Palette className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Page actions row (mobile) */}
      {children && (
        <div className="sm:hidden px-3 pb-2 flex items-center gap-2 overflow-x-auto no-scrollbar" data-testid="header-actions-mobile">
          {children}
        </div>
      )}

      {/* Mobile search row */}
      {showSearch && (
        <div className="md:hidden px-3 pb-3" data-testid="header-search-mobile-row">
          <form onSubmit={(e) => { e.preventDefault(); runSearch(e.currentTarget.querySelector('input')?.value); }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input data-testid="header-search-mobile" autoFocus placeholder="Buscar clientes..."
                className="pl-9 pr-16 h-10 bg-muted border-none text-sm"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(e.currentTarget.value); } }} />
              <button type="submit" data-testid="header-search-mobile-submit"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-7 px-3 rounded-md bg-primary text-primary-foreground text-xs font-semibold">
                Ir
              </button>
            </div>
          </form>
        </div>
      )}
    </header>
  );
};
